/**
 * One phone shape, everywhere — mirroring `backend/src/common/phone.ts`.
 *
 * v1 had no canonical form and stored the same number several ways, which is
 * why 96 accounts collided during the migration. The client normalises before
 * sending so v2 never repeats it; the server normalises again because a client
 * is not a validator.
 */
export interface CountryDialInfo {
  iso: string;
  name: string;
  dialCode: string;
  nationalLength: number;
  mobilePattern: RegExp;
  example: string;
  flag: string;
}

export const COUNTRIES: CountryDialInfo[] = [
  {
    iso: "NG",
    name: "Nigeria",
    dialCode: "234",
    nationalLength: 10,
    mobilePattern: /^[789][01]\d{8}$/,
    example: "0803 123 4567",
    flag: "🇳🇬",
  },
  {
    iso: "GH",
    name: "Ghana",
    dialCode: "233",
    nationalLength: 9,
    mobilePattern: /^[25]\d{8}$/,
    example: "024 123 4567",
    flag: "🇬🇭",
  },
  {
    iso: "RW",
    name: "Rwanda",
    dialCode: "250",
    nationalLength: 9,
    mobilePattern: /^7[2389]\d{7}$/,
    example: "078 123 4567",
    flag: "🇷🇼",
  },
];

export const DEFAULT_COUNTRY = COUNTRIES[0]!;

export function countryByIso(iso: string): CountryDialInfo | undefined {
  return COUNTRIES.find((country) => country.iso === iso.toUpperCase());
}

const byDialLength = [...COUNTRIES].sort((a, b) => b.dialCode.length - a.dialCode.length);

function countryByDialCode(digits: string): CountryDialInfo | undefined {
  return byDialLength.find((country) => digits.startsWith(country.dialCode));
}

/** Any local spelling to E.164, or null rather than a guess. */
export function normalisePhone(
  raw: string,
  country: CountryDialInfo = DEFAULT_COUNTRY,
): string | null {
  if (!raw) return null;

  const hadPlus = raw.trimStart().startsWith("+");
  const digits = raw.replace(/\D/g, "");
  if (!digits) return null;

  const international = countryByDialCode(digits);
  if (international) {
    const national = digits.slice(international.dialCode.length);
    if (national.length === international.nationalLength) {
      return `+${international.dialCode}${national}`;
    }
    if (national.startsWith("0") && national.length === international.nationalLength + 1) {
      return `+${international.dialCode}${national.slice(1)}`;
    }
  }

  if (hadPlus) return null;

  const national = digits.startsWith("0") ? digits.slice(1) : digits;
  if (national.length !== country.nationalLength) return null;

  return `+${country.dialCode}${national}`;
}

export function isValidMobile(raw: string, country: CountryDialInfo = DEFAULT_COUNTRY): boolean {
  const normalised = normalisePhone(raw, country);
  if (!normalised) return false;

  const digits = normalised.slice(1);
  const matched = countryByDialCode(digits);
  if (!matched) return false;

  return matched.mobilePattern.test(digits.slice(matched.dialCode.length));
}

/** What to tell someone whose number will not work — the fix, not the fault. */
export function phoneError(raw: string, country: CountryDialInfo = DEFAULT_COUNTRY): string | null {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 0) return null;

  // Compare against the length a student actually types, trunk zero included.
  const typedLength = digits.startsWith("0") ? country.nationalLength + 1 : country.nationalLength;
  if (digits.length < typedLength) {
    return `That number looks short — ${country.name} numbers have ${typedLength} digits.`;
  }
  if (!isValidMobile(raw, country)) {
    return `Check the number — that isn't a ${country.name} mobile.`;
  }
  return null;
}

/**
 * `+2348031234567` → `+234 803 123 4567`, for display only.
 *
 * Country-aware because a 9-digit Rwandan number groups differently from an
 * 11-digit Nigerian one, and a fixed 4-3-4 would misgroup both.
 */
export function displayPhone(e164: string): string {
  if (!e164.startsWith("+")) return e164;

  const digits = e164.slice(1);
  const country = byDialLength.find((entry) => digits.startsWith(entry.dialCode));
  if (!country) return e164;

  const national = digits.slice(country.dialCode.length);
  const sizes = national.length >= 10 ? [3, 3, 4] : [3, 3, 3];

  const groups: string[] = [];
  let offset = 0;
  for (const size of sizes) {
    if (offset >= national.length) break;
    groups.push(national.slice(offset, offset + size));
    offset += size;
  }
  if (offset < national.length) groups.push(national.slice(offset));

  return `+${country.dialCode} ${groups.join(" ")}`.trim();
}
